import { c as _c } from "react/compiler-runtime";
import { type PropsWithChildren, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { useTelemetryInfo, type TelemetryInfo } from "@/hooks/use-telemetry-info";
import { getRouteConfig, requiresIdentity } from "@/lib/telemetry-route-config";
import { initMixpanel, trackPageView, trackEmbeddedView, trackUser } from "@/lib/mixpanel-client";
import { normalizePath } from "@/lib/mixpanel-properties";
import * as Sentry from "@sentry/nextjs";

/**
 * `new URL()` requires an absolute base when the input is path-only (e.g. `/a?b=1` from Next).
 * Only `.pathname` is read; the origin is not used for root-relative paths.
 */
const AS_PATH_PARSE_BASE = "https://example.com";

/** Set Sentry identity tags and breadcrumbs. */
function setSentryIdentity(telemetryInfo: TelemetryInfo) {
  const {
    user,
    company
  } = telemetryInfo;
  Sentry.addBreadcrumb({
    data: {
      telemetryInfo
    }
  });
  if (user?.id) {
    Sentry.setTag("userId", String(user.id));
  }
  if (user?.email) {
    Sentry.setTag("userEmail", user.email);
  }
  if (company?.name) {
    Sentry.setTag("companyName", company.name);
  }
  if (company?.id) {
    Sentry.setTag("companyId", String(company.id));
  }
}

/**
 * Central telemetry orchestrator. Handles Mixpanel init, identity setup,
 * and page event firing based on a static route config.
 *
 * Pages don't need any telemetry hooks — just add the route to
 * `lib/telemetry-route-config.ts`.
 */
export function TelemetryProvider(t0) {
  const $ = _c(29);
  const {
    children
  } = t0;
  const router = useRouter();
  let persona;
  let routeConfig;
  let t1;
  if ($[0] !== router.pathname) {
    routeConfig = getRouteConfig(router.pathname);
    ({
      persona
    } = routeConfig);
    t1 = requiresIdentity(persona);
    $[0] = router.pathname;
    $[1] = persona;
    $[2] = routeConfig;
    $[3] = t1;
  } else {
    persona = $[1];
    routeConfig = $[2];
    t1 = $[3];
  }
  const shouldFetchIdentity = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(_temp, t2);
  let t3;
  if ($[5] !== shouldFetchIdentity) {
    t3 = {
      enabled: shouldFetchIdentity
    };
    $[5] = shouldFetchIdentity;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data: telemetryInfo
  } = useTelemetryInfo(t3);
  const identifiedRef = useRef(false);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      identifiedRef.current = false;
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== shouldFetchIdentity) {
    t5 = [shouldFetchIdentity];
    $[8] = shouldFetchIdentity;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[10] !== telemetryInfo) {
    t6 = () => {
      if (telemetryInfo && !identifiedRef.current) {
        trackUser(telemetryInfo);
        setSentryIdentity(telemetryInfo);
        identifiedRef.current = true;
      }
    };
    t7 = [telemetryInfo];
    $[10] = telemetryInfo;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[13] !== persona || $[14] !== router.asPath || $[15] !== router.events) {
    t8 = () => {
      if (persona === "embedded" || persona === "excluded") {
        return;
      }
      trackPageView(router.asPath);
      const handleRouteChange = _temp2;
      router.events.on("routeChangeComplete", handleRouteChange);
      return () => {
        router.events.off("routeChangeComplete", handleRouteChange);
      };
    };
    t9 = [router.events, router.asPath, persona];
    $[13] = persona;
    $[14] = router.asPath;
    $[15] = router.events;
    $[16] = t8;
    $[17] = t9;
  } else {
    t8 = $[16];
    t9 = $[17];
  }
  useEffect(t8, t9);
  const embeddedFiredRef = useRef(false);
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      embeddedFiredRef.current = false;
    };
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== router.pathname) {
    t11 = [router.pathname];
    $[19] = router.pathname;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[21] !== persona || $[22] !== routeConfig.viewName || $[23] !== router.asPath || $[24] !== telemetryInfo) {
    t12 = () => {
      if (persona !== "embedded") {
        return;
      }
      if (!telemetryInfo) {
        return;
      }
      if (embeddedFiredRef.current) {
        return;
      }
      if (!routeConfig.viewName) {
        return;
      }
      const cleanPath = new URL(router.asPath, AS_PATH_PARSE_BASE).pathname;
      const normalizedPath = normalizePath(cleanPath);
      const config = {
        mode: "embedded" as const,
        viewName: routeConfig.viewName
      };
      const initialized = initMixpanel();
      if (initialized) {
        trackEmbeddedView({
          normalizedPath,
          config
        });
        embeddedFiredRef.current = true;
      }
    };
    t13 = [persona, telemetryInfo, router.asPath, routeConfig.viewName];
    $[21] = persona;
    $[22] = routeConfig.viewName;
    $[23] = router.asPath;
    $[24] = telemetryInfo;
    $[25] = t12;
    $[26] = t13;
  } else {
    t12 = $[25];
    t13 = $[26];
  }
  useEffect(t12, t13);
  let t14;
  if ($[27] !== children) {
    t14 = <>{children}</>;
    $[27] = children;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
}
function _temp2(url) {
  const newPathname = new URL(url, AS_PATH_PARSE_BASE).pathname;
  const newConfig = getRouteConfig(newPathname);
  if (newConfig.persona === "embedded" || newConfig.persona === "excluded") {
    return;
  }
  trackPageView(url);
  if (typeof window !== "undefined" && window?.Intercom) {
    window.Intercom?.("update");
  }
}
function _temp() {
  initMixpanel();
}